import React, { useCallback, useRef, useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import Markdown from 'react-markdown';
import styled, { createGlobalStyle } from 'styled-components';
import { AppModal, AppNoInfo, AppRemixIcon } from '@/layout/AppComponents';
import colors from '@/constants/colors';
import images from '@/constants/images';
import Translator from '@/layout/components/helpers/Translator';
import axios from 'axios';
import { axiosPath } from '@/routing/helpers/router';
import useIsMounted from '@/layout/components/hooks/useIsMounted';
import { useParams } from 'react-router-dom';

const QuestionChatbotModalStyles = createGlobalStyle`
  .modal-dialog.question-chatbot-dialog {

    height: 80vh;
    margin: 10vh auto;
  }
  .question-chatbot-content {
    height: 100%;
    display: flex;
    flex-direction: column;
  }
  .question-chatbot-content .modal-body {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }
`;

export default function QuestionChatbot({ question }) {
  const { moduleBridgeHex, plantHex } = useParams();
  const isMounted = useIsMounted();
  const [showChatbotModal, setShowChatbotModal] = useState(false);
  const textAreaRef = useRef(null);
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [conversationID, setConversationID] = useState(null);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  const handleSubmit = useCallback(async () => {
    const msgs = [{ text: inputValue, isUser: true }, ...messages];
    setMessages(msgs);
    setInputValue('');
    setIsLoading(true);
    axios
      .post(
        axiosPath(
          'api_r_user_m_legalCompliance_plantObligationForm_chatbot_chat',
        ),
        {
          userQuestion: inputValue,
          moduleBridgeHex,
          plantHex,
          conversationID,
          questionID: question.id,
        },
      )
      .then(({ data }) => {
        if (isMounted.current) {
          setIsLoading(false);
          setMessages([{ text: data.text, isUser: false }, ...msgs]);
          setConversationID(data?.conversationID);
          textAreaRef.current.focus();
        }
      })
      .catch((e) => {
        setIsLoading(false);
        setMessages([
          { text: 'Error', isUser: false, error: true },
          ...messages,
        ]);
      });
  }, [
    inputValue,
    messages,
    moduleBridgeHex,
    plantHex,
    conversationID,
    isMounted,
  ]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const resetChat = useCallback(() => {
    setMessages([]);
    setInputValue('');
    setIsLoading(false);
    setConversationID(null);
    setShowCloseConfirm(false);
  }, []);

  const closeChat = useCallback(() => {
    setShowChatbotModal(false);
    resetChat();
  }, [resetChat]);

  const handleModalClose = useCallback(() => {
    const hasConversation =
      messages.length > 0 || inputValue.trim() !== '' || isLoading;

    if (!hasConversation) {
      closeChat();
      return;
    }
    if (showCloseConfirm) {
      setShowCloseConfirm(false);
      return;
    }
    setShowCloseConfirm(true);
  }, [messages, inputValue, isLoading, showCloseConfirm, closeChat]);

  return (
    <>
      <span
        className="btn btn-info btn-sm mb-2 mr-2"
        title={ReactDOMServer.renderToString(
          <Translator
            file="module-legalCompliance"
            name="m-gcl-type.question.waibox.help"
          />,
        )}
        onClick={() => setShowChatbotModal(true)}
      >
        <AppRemixIcon
          icon="question-answer"
          filled
          className="pointer"
        />
      </span>
      {showChatbotModal && (
        <>
          <QuestionChatbotModalStyles />
          <AppModal
            show={showChatbotModal}
            onClose={handleModalClose}
            title="WAI-Box"
            size="xl"
            dialogClassName="question-chatbot-dialog"
            contentClassName="question-chatbot-content"
          >
            <ChatContainer>
              <p>
                <Translator
                  file="module-legalCompliance"
                  name="m-gcl-chat.waibox.ai.answers.question"
                />
                <br /> <strong>{question.name}</strong>.
              </p>
              <ChatbotAdvise>
                <Translator
                  file="module-legalCompliance"
                  name="module.lc.chatbot.non-anonymous-conversations"
                />
              </ChatbotAdvise>
              <ChatWindow>
                {showCloseConfirm && (
                  <CloseConfirmOverlay>
                    <CloseConfirmCard>
                      <AppRemixIcon
                        icon="alert"
                        color={colors.primary}
                        size={34}
                      />
                      <p>
                        <Translator
                          file="module-legalCompliance"
                          name="m-gcl-close.chat.conversation.lost"
                        />
                      </p>
                      <div className="d-flex gap-2 justify-content-end">
                        <button
                          type="button"
                          className="btn btn-default"
                          onClick={() => setShowCloseConfirm(false)}
                        >
                          <Translator
                            file="module-legalCompliance"
                            name="m-gcl-continue.chat"
                          />
                        </button>
                        <button
                          type="button"
                          className="btn btn-danger"
                          onClick={closeChat}
                        >
                          <Translator
                            file="module-legalCompliance"
                            name="m-gcl-close.lose"
                          />
                        </button>
                      </div>
                    </CloseConfirmCard>
                  </CloseConfirmOverlay>
                )}
                {messages.length > 0 ? (
                  <Messages>
                    {isLoading && (
                      <MessageWrapper isUser={false}>
                        <Avatar alt="WAI-Box" src={images.waibox} />
                        <MessageBubble isUser={false}>
                          <Loader height="20" width="40">
                            {[10, 20, 30].map((c) => (
                              <circle
                                key={c}
                                className="dot"
                                cx={c}
                                cy="10"
                                r="2"
                                style={{ fill: colors.primary }}
                              />
                            ))}
                          </Loader>
                        </MessageBubble>
                      </MessageWrapper>
                    )}
                    {messages.map((message, index) => (
                      <MessageWrapper key={index} isUser={message.isUser}>
                        {!message.isUser && (
                          <Avatar alt="WAI-Box" src={images.waibox} />
                        )}
                        <MessageBubble
                          isUser={message.isUser}
                          error={message.error}
                        >
                          {message.isUser ? (
                            message.text
                          ) : (
                            <Markdown>{message.text}</Markdown>
                          )}
                        </MessageBubble>
                        {message.isUser && (
                          <Avatar alt="Usuario" src={images.defaultUser} />
                        )}
                      </MessageWrapper>
                    ))}
                  </Messages>
                ) : (
                  <AppNoInfo
                    icon={<AppRemixIcon icon="robot-3" size={34} filled />}
                    text={
                      <Translator
                        file="module-legalCompliance"
                        name="m-gcl-type.question.waibox.help"
                      />
                    }
                  />
                )}
                <InputContainer>
                  <TextArea
                    ref={textAreaRef}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyPress}
                    placeholder={ReactDOMServer.renderToString(
                      <Translator
                        file="module-legalCompliance"
                        name="module.lc.chatbot.write-message"
                      />,
                    )}
                    rows="1"
                    disabled={isLoading}
                  />
                  <Button
                    onClick={handleSubmit}
                    className="btn btn-info"
                    disabled={isLoading}
                  >
                    <AppRemixIcon icon="send-plane-2" filled />
                  </Button>
                </InputContainer>
              </ChatWindow>
            </ChatContainer>
          </AppModal>
        </>
      )}
    </>
  );
}

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
  min-height: 0;
  position: relative;
`;

const ChatbotAdvise = styled.div`
  font-size: 14px;
  color: ${colors.grey500};
  text-align: center;
  flex-shrink: 0;
`;

const ChatWindow = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  background-color: white;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  display: flex;
  flex-direction: column;
`;

const CloseConfirmOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  inset: 0;
  z-index: 10;
  display: flex;
  justify-content: center;
  background-color: rgba(255, 255, 255, 1);
  border-radius: 10px;
`;

const CloseConfirmCard = styled.div`
  /* max-width: 320px; */
  padding: 20px;
  text-align: center;
  p {
    margin: 12px 0 16px;
  }
`;

const Messages = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 10px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column-reverse;
`;

const MessageWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  margin-bottom: 16px;
  justify-content: ${({ isUser }) => (isUser ? 'flex-end' : 'flex-start')};

  &:first-child {
    margin-bottom: 16px;
  }
`;

const bubbleBg = ({ isUser, error }) =>
  error ? 'red' : isUser ? '#dcf8c6' : colors.grey300;

const MessageBubble = styled.div`
  max-width: ${({ isUser }) => (isUser ? '60%' : '85%')};
  padding: 10px;
  border-radius: 10px;
  position: relative;
  background-color: ${bubbleBg};

  p {
    margin: 0 0 0.5em;
    &:last-child {
      margin-bottom: 0;
    }
  }
  ul,
  ol {
    margin: 0.25em 0 0.5em;
    padding-left: 1.25em;
  }
  li {
    margin-bottom: 0.25em;
  }
  strong {
    font-weight: 700;
  }

  &::after {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
    border-style: solid;
    bottom: 10px;
    ${({ isUser, error }) =>
      isUser
        ? `
      right: -10px;
      border-width: 10px 0 10px 10px;
      border-color: transparent transparent transparent #dcf8c6;
    `
        : `
      left: -10px;
      border-width: 10px 10px 10px 0;
      border-color: transparent ${error ? 'red' : colors.grey300} transparent transparent;
    `}
  }
`;

const Avatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #ccc;
  margin: 0 10px;
  flex-shrink: 0;
`;

const InputContainer = styled.div`
  display: flex;
  padding: 10px;
  border-top: 1px solid #ccc;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const TextArea = styled.textarea`
  flex: 1;
  padding: 10px;
  font-size: 16px;
  border: 1px solid #ccc;
  border-radius: 4px;
  resize: none;
  overflow: hidden;
  line-height: 1.5;
`;

const Button = styled.button`
  padding: 10px 20px;
  margin-left: 10px;
  font-size: 16px;
  border: none;
  border-radius: 4px;

  &:hover {
    background-color: #0056b3;
  }
`;

const Loader = styled.svg`
  @keyframes blink {
    50% {
      fill: transparent;
    }
  }
  .dot {
    animation: 1s blink infinite;
    fill: grey;
  }
  .dot:nth-child(2) {
    animation-delay: 250ms;
  }
  .dot:nth-child(3) {
    animation-delay: 500ms;
  }
`;
